import { useMemo } from 'react';

import { competencyTransform } from '@backrs_ui/common-ui';

import internshipGuideBanner from '@backrs_ui/grow-ui/src/assets/images/internships/internships-guide-banner.png';
import {
  GuideNuggetRow,
  GuidePageShell,
  GuideSections,
  GuideToolsSection,
} from '@backrs_ui/grow-ui/src/components/GuidePage';
import { LubavButton } from '@backrs_ui/grow-ui/src/components/Lubav/LubavButton';
import { StickerCardId } from '@backrs_ui/grow-ui/src/pages/Home/hooks/stickerCardMapping';
import { selectContentCompetencies } from '@backrs_ui/grow-ui/src/redux/contentCompetency/contentCompetency';
import { useAppSelector } from '@backrs_ui/grow-ui/src/redux/hooks';

import {
  AdviceFromAProSection,
  MythsVsRealitySection,
  OverviewStatCards,
  SixStepProcessSection,
} from './components';
import { useAgentContext } from './InternshipGuidePageContainer.agentContext';
import { internshipGuideContent } from '../content';

const SECTION_IDS = {
  overview: 'internship-guide-section-overview',
  myths: 'internship-guide-section-myths',
  tools: 'internship-guide-section-tools',
  sixStep: 'internship-guide-section-six-step',
  skills: 'internship-guide-section-skills',
  advice: 'internship-guide-section-advice',
} as const;

const INTERNSHIP_GUIDE_TOOL_ID = 'internships_guide';

const INTERNSHIP_TOOL_IDS: readonly StickerCardId[] = [
  'discover_jobs_sticker',
  'resume_builder',
  'resume_optimizer',
  'boost_your_profile',
];

export const InternshipGuidePageContainer = () => {
  const { contentCompetencies } = useAppSelector(selectContentCompetencies);

  const skillNuggets = useMemo(() => {
    const bySlug = new Map(contentCompetencies.map((competency) => [competency.slug, competency]));

    return internshipGuideContent.skillBuilding.competencySlugs
      .map((slug) => bySlug.get(slug))
      .filter((competency): competency is NonNullable<typeof competency> => Boolean(competency))
      .map(competencyTransform);
  }, [contentCompetencies]);

  useAgentContext({ skillNuggets });

  const sections = [
    {
      id: SECTION_IDS.myths,
      title: internshipGuideContent.mythsVsReality.title,
      subtitle: internshipGuideContent.mythsVsReality.subtitle,
      rightSlot: (
        <LubavButton
          id="internship-guide-myths-lubav-btn"
          label="Discuss with Lubav"
          prompt="I just read the Internship Myths vs Reality section. Based on what you know about me, identify the single myth I'm most likely falling for and focus only on that one. Briefly explain why it applies to me and what I should do differently in my internship search. Do not cover any other myths."
          allowTools={false}
        />
      ),
      content: <MythsVsRealitySection content={internshipGuideContent.mythsVsReality} />,
    },
    {
      id: SECTION_IDS.tools,
      title: internshipGuideContent.internshipTools.title,
      subtitle: internshipGuideContent.internshipTools.subtitle,
      rightSlot: (
        <LubavButton
          id="internship-guide-tools-lubav-btn"
          label="Which tool should I start with?"
          prompt="Which of these internship tools should I start with?"
          allowTools={false}
        />
      ),
      content: <GuideToolsSection toolIds={INTERNSHIP_TOOL_IDS} />,
    },
    {
      id: SECTION_IDS.sixStep,
      title: internshipGuideContent.sixStepProcess.title,
      subtitle: internshipGuideContent.sixStepProcess.subtitle,
      rightSlot: (
        <LubavButton
          id="internship-guide-six-step-lubav-btn"
          label="Personalize for me"
          prompt="Using my profile information, create a personalized version of this six-step process for landing an internship. Keep the description of each step very concise—just one or two short sentences that tailor it to my background, goals, intended major or career path, and current stage. After listing all six steps, ask me whether I'd like to dive deeper into any one of them."
        />
      ),
      content: (
        <SixStepProcessSection
          content={internshipGuideContent.sixStepProcess}
          conclusion={internshipGuideContent.conclusion}
        />
      ),
    },
    {
      id: SECTION_IDS.skills,
      title: internshipGuideContent.skillBuilding.title,
      subtitle: internshipGuideContent.skillBuilding.subtitle,
      rightSlot: (
        <LubavButton
          id="internship-guide-skill-building-lubav-btn"
          label="Which should I build?"
          prompt="Using my profile along with my Are You On Track results and my Blueprint, determine which Backrs competencies would help me most for landing and succeeding in an internship. Recommend the specific competencies I should focus on building first and briefly explain why each one matters for my situation."
          sx={{ whiteSpace: 'nowrap', justifyContent: 'flex-start' }}
        />
      ),
      content: <GuideNuggetRow nuggets={skillNuggets} />,
    },
    {
      id: SECTION_IDS.advice,
      title: internshipGuideContent.adviceFromAPro.title,
      subtitle: internshipGuideContent.adviceFromAPro.subtitle,
      rightSlot: (
        <LubavButton
          id="internship-guide-advice-lubav-btn"
          label="How does this apply to me?"
          prompt="Based on the advice Tom Latkovic shares in the 'Advice from a Pro' video on this page and what you know about me — my profile, goals, intended career path, and current stage — pick the three pieces of his advice that matter most for me right now. Start with a brief, conversational one-sentence intro, then for each of the three, briefly explain why it applies to me and give one concrete next step, without inventing precise schedules, quotas, or counts that Tom didn't say. Then ask if I'd like to go deeper on any of the three."
          allowTools={false}
          sx={{ whiteSpace: 'nowrap', justifyContent: 'flex-start' }}
        />
      ),
      content: <AdviceFromAProSection content={internshipGuideContent.adviceFromAPro} />,
    },
  ];

  return (
    <GuidePageShell
      toolId={INTERNSHIP_GUIDE_TOOL_ID}
      banner={{ src: internshipGuideBanner, alt: 'Backrs Guide to Landing a Great Internship' }}
    >
      <GuideSections
        header={internshipGuideContent.header}
        overview={{
          id: SECTION_IDS.overview,
          extra: <OverviewStatCards stats={internshipGuideContent.overviewStats} />,
        }}
        sections={sections}
      />
    </GuidePageShell>
  );
};
